import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type UserCredential,
} from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { authService, type UpdateAuthUserInput } from '@/services/auth.service';
import { sessionService } from '@/services/session.service';
import type { CreateWorkspaceInput } from '@/services/workspace.service';
import { mapErrorMessage } from '@/lib/mapApiError';
import { classifyGoogleSignInError, type AuthSessionMode } from '@/lib/googleSignInError';
import { AppError as HttpAppError, normalizeAxiosError } from '@/http/errors';
import type { AppDispatch } from '@/app/store';
import type { AppError, AuthUser } from './types';

type RegisterPayload = { email: string; password: string; remember?: boolean };

function mapFirebaseError(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'errors.emailInUse';
    case 'auth/invalid-email':
      return 'errors.invalidEmail';
    case 'auth/weak-password':
      return 'errors.weakPassword';
    case 'auth/network-request-failed':
      return 'errors.network';
    case 'auth/too-many-requests':
      return 'errors.tooManyRequests';
    case 'auth/invalid-credential':
      return 'errors.invalidCredentials';
    case 'auth/user-not-found':
      return 'errors.userNotFound';
    case 'auth/expired-action-code':
      return 'errors.expiredActionCode';
    case 'auth/invalid-action-code':
      return 'errors.invalidActionCode';
    case 'auth/user-disabled':
      return 'errors.userDisabled';
    default:
      return 'errors.generic';
  }
}

export const bootstrap = createAsyncThunk<AuthUser | null, void, { dispatch: AppDispatch }>(
  'auth/bootstrap',
  async (_, { dispatch }) => {
    try {
      const user = await authService.me();

      if (user) {
        const { fetchUserWorkspaces } = await import('@/features/workspace/workspaceThunks');
        dispatch(fetchUserWorkspaces({ page: 0 }));
      }

      return user;
    } catch {
      return null;
    }
  },
);

export const loginEmail = createAsyncThunk<
  AuthUser,
  { email: string; password: string },
  { rejectValue: AppError; dispatch: AppDispatch }
>('auth/loginEmail', async ({ email, password }, { rejectWithValue, dispatch }) => {
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);

    const idToken = await cred.user.getIdToken();
    await sessionService.setSession(idToken, 'login');
    const user = await authService.me();

    if (user) {
      const { fetchUserWorkspaces } = await import('@/features/workspace/workspaceThunks');
      await dispatch(fetchUserWorkspaces({ page: 0 }));
    }

    return user;
  } catch (err) {
    const e = err as { code?: string; message?: string };
    const key = e.code ? mapFirebaseError(e.code) : 'errors.generic';
    return rejectWithValue({ code: e.code ?? 'unknown', message: key });
  }
});

export const loginGoogle = createAsyncThunk<
  AuthUser,
  { mode: AuthSessionMode },
  { rejectValue: AppError; dispatch: AppDispatch; fulfilledMeta: { isNewUser: boolean } }
>('auth/loginGoogle', async ({ mode }, { rejectWithValue, dispatch, fulfillWithValue }) => {
  try {
    const cred = await signInWithPopup(auth, googleProvider);
    const idToken = await cred.user.getIdToken();
    await sessionService.setSession(idToken, mode);
    const user = await authService.me();

    if (user) {
      const { fetchUserWorkspaces } = await import('@/features/workspace/workspaceThunks');
      await dispatch(fetchUserWorkspaces({ page: 0 }));
    }

    return fulfillWithValue(user, { isNewUser: getAdditionalUserInfo(cred)?.isNewUser ?? false });
  } catch (err) {
    const failure = classifyGoogleSignInError(err);
    if (failure === 'cancelled') {
      return rejectWithValue({ code: 'cancelled', message: '' });
    }
    if (failure === 'notRegistered') {
      // Le back supprime lui-même le compte Firebase refusé, il ne reste qu'à fermer la session.
      await signOut(auth);
      return rejectWithValue({ code: 'notRegistered', message: 'errors.googleNotRegistered' });
    }
    const e = err as { code?: string };
    const key = e.code ? mapFirebaseError(e.code) : 'errors.generic';
    return rejectWithValue({ code: e.code ?? 'unknown', message: key });
  }
});

export const logout = createAsyncThunk<void>('auth/logout', async () => {
  await authService.logout();
  await signOut(auth);
});

export const forgotPassword = createAsyncThunk<void, { email: string }, { rejectValue: AppError }>(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      await authService.forgotPassword(email);
      return;
    } catch (err) {
      const e = err as { code?: string; message?: string };
      return rejectWithValue({ code: e.code ?? 'unknown', message: e.message ?? 'errors.generic' });
    }
  },
);

export const updatePassword = createAsyncThunk<
  void,
  { oobCode: string; password: string },
  { rejectValue: AppError }
>('auth/updatePassword', async ({ oobCode, password }, { rejectWithValue }) => {
  try {
    await confirmPasswordReset(auth, oobCode, password);

    return;
  } catch (err) {
    const e = err as { code?: string; message?: string };
    const key = e.code ? mapFirebaseError(e.code) : 'errors.generic';
    return rejectWithValue({ code: e.code ?? 'unknown', message: key });
  }
});

export const registerEmail = createAsyncThunk<AuthUser, RegisterPayload, { rejectValue: AppError }>(
  'auth/registerEmail',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const cred: UserCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = cred.user;
      if (!user) {
        return rejectWithValue({ code: 'no-user', message: 'errors.generic' });
      }
      const idToken = await user.getIdToken(true);
      await sessionService.setSession(idToken, 'register');
      return await authService.me();
    } catch (err) {
      const e = err as { code?: string; message?: string };
      const key = e.code ? mapFirebaseError(e.code) : 'errors.generic';
      return rejectWithValue({ code: e.code ?? 'unknown', message: key });
    }
  },
);

export const updateAuthUser = createAsyncThunk<
  AuthUser,
  UpdateAuthUserInput,
  { rejectValue: AppError }
>('auth/updateAuthUser', async (input, { rejectWithValue }) => {
  try {
    await authService.updateAuthUser(input);
    return await authService.me();
  } catch (err) {
    const error = normalizeAxiosError(err);
    return rejectWithValue({
      code: error.code,
      message: error.message,
      details: error.details,
      status: error.status,
    });
  }
});

export const updatePictureAuthUser = createAsyncThunk<AuthUser, File, { rejectValue: AppError }>(
  'auth/updatePictureAuthUser',
  async (file, { rejectWithValue }) => {
    try {
      return await authService.updatePictureAuthUser(file);
    } catch (err) {
      return rejectWithValue({ code: 'unknown', message: 'errors.generic' });
    }
  },
);

export const deletePictureAuthUser = createAsyncThunk<AuthUser, void, { rejectValue: AppError }>(
  'auth/deletePictureAuthUser',
  async (_, { rejectWithValue }) => {
    try {
      return await authService.deletePictureAuthUser();
    } catch (err) {
      return rejectWithValue({ code: 'unknown', message: 'errors.generic' });
    }
  },
);

export const createWorkspace = createAsyncThunk<
  AuthUser,
  CreateWorkspaceInput,
  { rejectValue: AppError; dispatch: AppDispatch }
>('auth/createWorkspace', async (input, { rejectWithValue, dispatch }) => {
  try {
    const { createWorkspaceThunk } = await import('@/features/workspace/workspaceThunks');
    await dispatch(createWorkspaceThunk(input));
    const user = await authService.me();

    if (user) {
      const { fetchUserWorkspaces } = await import('@/features/workspace/workspaceThunks');
      dispatch(fetchUserWorkspaces({ page: 0 }));
    }

    return user;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});
